import { validateCmsItinerariesSearch } from '@cms/features/cms-itineraries-filters';
import { validateCmsProductsSearch } from '@cms/features/cms-products-filters';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { protectedRoute } from './_root';

export const cmsProductsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/products',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-products').then((m) => ({
      default: m.CmsProductsPage,
    }))
  ),
  validateSearch: validateCmsProductsSearch,
});

export const cmsProductDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/products/$id',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-product-detail').then((m) => ({
      default: m.CmsProductDetailPage,
    }))
  ),
});

export const cmsItinerariesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/itineraries',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-itineraries').then((m) => ({
      default: m.CmsItinerariesPage,
    }))
  ),
  validateSearch: validateCmsItinerariesSearch,
});

// Legacy `cms/pages` → renamed to `cms/static-pages` (issue #281). Kept as a
// redirect so existing bookmarks resolve.
export const cmsPagesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/pages',
  beforeLoad: () => {
    throw redirect({ to: '/cms/static-pages' });
  },
});

export const cmsTaxonomyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/taxonomy',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-taxonomy').then((m) => ({
      default: m.CmsTaxonomyPage,
    }))
  ),
});

// CMS landing-pages tab (issue #280). All 9 `/cms/*` tab routes are
// registered up front so the `cms-nav` strip never 404s; every tab now
// mounts its real page (the F3 "coming soon" placeholders are gone).
export const cmsLandingPagesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/landing-pages',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-landing-pages').then((m) => ({
      default: m.CmsLandingPagesPage,
    }))
  ),
});

// `$slug` carries the URL-encoded page slug. The list page encodes it
// (`landing/morocco` → `landing%2Fmorocco`) so the slash survives as a
// single path segment; the editor page decodes it back.
export const cmsLandingPageEditorRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/landing-pages/$slug',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-landing-page-editor').then((m) => ({
      default: m.CmsLandingPageEditorPage,
    }))
  ),
});

export const cmsStaticPagesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/static-pages',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-static-pages').then((m) => ({
      default: m.CmsStaticPagesPage,
    }))
  ),
});

export const cmsStaticPageEditorRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/static-pages/$slug',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-static-page-editor').then((m) => ({
      default: m.CmsStaticPageEditorPage,
    }))
  ),
  // `country` selects the per-country variant row (`about-us/<country>`,
  // `reviews/<country>`) — legacy `?country=` behaviour.
  validateSearch: (search: Record<string, unknown>): { country?: string } =>
    typeof search.country === 'string' && search.country.length > 0
      ? { country: search.country }
      : {},
});

export const cmsHashtagAssociationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/hashtag-associations',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-hashtag-associations').then((m) => ({
      default: m.CmsHashtagAssociationsPage,
    }))
  ),
});

export const cmsMetaInfoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/meta-info',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-meta-info').then((m) => ({
      default: m.CmsMetaInfoPage,
    }))
  ),
});

export const cmsCatalogTranslateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/catalog-translate',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-catalog-translate').then((m) => ({
      default: m.CmsCatalogTranslatePage,
    }))
  ),
});

export const cmsMarketingRegenerateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/marketing-regenerate',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-marketing-regenerate').then((m) => ({
      default: m.CmsMarketingRegeneratePage,
    }))
  ),
});

export const cmsSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'cms/settings',
  component: lazyRouteComponent(() =>
    import('@cms/pages/cms-settings').then((m) => ({
      default: m.CmsSettingsPage,
    }))
  ),
});
